import Constants from '@intercom/embercom-composer/lib/constants';
import { jsonStringify } from '@intercom/pulse/lib/computed-properties';
import BlockList from './blocks/block-list';
import AttachmentList from './blocks/attachment-list';
import BlockSelection from './blocks/block-selection';
import UndoStack from 'ember-undo-stack/undo-stack';
import ReturnCommand from './blocks/commands/return';
import BackspaceCommand from './blocks/commands/backspace';
import DeleteCommand from './blocks/commands/delete';
import DeleteWordCommand from './blocks/commands/delete-word';
import BackspaceWordCommand from './blocks/commands/backspace-word';
import InsertCharacterCommand from './blocks/commands/insert-character';
import InsertBlockCommand from './blocks/commands/insert-block';
import InsertBlocksCommand from './blocks/commands/insert-blocks';
import InsertBlocksAtMarkerCommand from './blocks/commands/insert-blocks-at-marker';
import InsertAttributeCommand from './blocks/commands/insert-attribute';
import CreateBlockCommand from './blocks/commands/create-block';
import ConvertSelectionCommand from './blocks/commands/convert-selection';
import CreateEntityCommand from './blocks/commands/create-entity';
import PasteCommand from './blocks/commands/paste';
import SyncCommand from './blocks/commands/sync';
import SelectEntityCommand from './blocks/commands/select-entity';
import AlignCommand from './blocks/commands/align';
import CurrentEditableObject from '@intercom/embercom-composer/blocks/mixins/current-editable-object';
import { reads, notEmpty, alias, equal, gt } from '@ember/object/computed';
import Em from 'ember';
export default Em.Object.extend(Em.Evented, UndoStack, {
  undoStackMaxDepth: 100,
  undoCheckpointThrottleInMilliseconds: 1200,
  html: reads('blockList.html'),
  htmlWithoutIDs: reads('blockList.htmlWithoutIDs'),
  htmlWithPreview: reads('blockList.htmlWithPreview'),
  lastUpdated: reads('blockList.lastUpdated'),
  selectionInfo: reads('selection.info'),
  bomSelection: reads('selection.bomSelection'),
  entityInfo: reads('blockList.entityInfo'),
  jsonBlocks: jsonStringify('serialized'),
  serialized: Em.computed('blockList.serialized', 'attachmentList.serialized', 'attachmentList.length', function () {
    if (this.get('attachmentList.length')) {
      return this.get('blockList.serialized').concat(this.get('attachmentList.serialized')).compact();
    }
    return this.get('blockList.serialized').compact();
  }),
  jsonBlocksForUndo: jsonStringify('serializedForUndo'),
  serializedForUndo: Em.computed('blockList.serializedWithEmptyTrailingBlocks', 'attachmentList.serialized', 'attachmentList.length', function () {
    if (this.get('attachmentList.length')) {
      return this.get('blockList.serializedWithEmptyTrailingBlocks').concat(this.get('attachmentList.serialized')).compact();
    }
    return this.get('blockList.serializedWithEmptyTrailingBlocks').compact();
  }),
  hasContent: reads('blockList.hasContent'),
  caretBlock: reads('selection.firstSelectedBlock'),
  selectedBlocks: reads('selection.selectedBlocks'),
  hasImageErrors: notEmpty('blockList.imageLoadErrors'),
  hasMarkers: alias('blockList.hasMarkers'),
  selectedEntities: reads('selection.selectedEntities'),
  currentEditableObjectOrSelectionChanged: reads('selection.currentEditableObjectOrSelectionChanged'),
  currentEditableEntity: reads('selection.currentEditableEntity'),
  //TODO: GJ: remove when all editors use `currentEditableObject`
  currentEditableBlock: reads('selection.currentEditableBlock'),
  currentEditableObject: reads('selection.currentEditableObject'),
  hasCurrentEditableObject: reads('selection.hasCurrentEditableObject'),
  hasNoRedoStack: equal('redoStack.length', 0),
  setup: Em.on('init', function () {
    this.set('blockList', BlockList.deserialize(this.get('blocks'), this.get('config'), this.get('uploadManager')));
    this.set('attachmentList', AttachmentList.deserialize(this.get('blocks'), this.get('config'), this.get('uploadManager')));
    this.get('blockList')._ensureDefaultBlock();
    let selection = BlockSelection.extend(CurrentEditableObject).create();
    selection.set('blockList', this.get('blockList'));
    this.set('selection', selection);
    this.get('lastUpdated');
    this.set('isInitialized', true);
    this.set('_hasAttachments', false);
  }),
  load(blocks, keepUndoStack) {
    this._resetSkips();
    this.set('blocks', blocks);
    this.setup();
    if (!keepUndoStack) {
      this._undo_stack_setup();
    }
  },
  sync(html, selection) {
    return this._sync(html, selection, true);
  },
  syncWithoutCheckpoint(html, selection) {
    return this._sync(html, selection, false);
  },
  _sync(html, selection, shouldCheckpoint) {
    if (Em.isNone(html)) {
      throw new Error('`html` was not specified');
    }
    if (Em.isNone(selection)) {
      throw new Error('`selection` was not specified');
    }
    let syncResult;
    Em.instrument('Composer.composerState.sync', null, function () {
      if (shouldCheckpoint) {
        this.throttledCheckpoint();
      }
      let newSelection = BlockSelection.create();
      newSelection.setProperties(selection);
      newSelection.set('blockList', this.get('blockList'));
      syncResult = this._runCommand(SyncCommand, {
        html
      }, newSelection);
      this.get('selection').setProperties(newSelection.getSelectionHash());
    }.bind(this));
    this.get('selection').recalculateCurrentEditableEntity();
    return syncResult;
  },
  // Sometimes we want to do a series of operations on the composer content, and sometimes across run loops.
  // If we were to invoke `onChange` actions while undergoing these operations, a consumer could call `getBlocks`
  // which could interfere with the internal composer state. An example is the `cut` operation: https://github.com/intercom/intercom/issues/117525#issuecomment-425005715
  // We can control the surpression of `onChange` actions with `syncLock`, `syncUnlock` and `resetSyncLock`
  syncLockSemaphore: 0,
  isSyncLocked: gt('syncLockSemaphore', 0),
  syncLock() {
    this.incrementProperty('syncLockSemaphore');
  },
  syncUnlock() {
    if (this.get('syncLockSemaphore') > 0) {
      this.decrementProperty('syncLockSemaphore');
    }
  },
  resetSyncLock() {
    this.set('syncLockSemaphore', 0);
  },
  setSelection(startBlock, startItem, startPosition, endBlock, endItem, endPosition) {
    let selection = this.get('selection');
    let blockList = this.get('blockList');
    blockList._ensureDefaultBlock();
    let newSelection = BlockSelection.create();
    newSelection.set('blockList', blockList);
    newSelection.setProperties({
      startBlockIndex: startBlock,
      startItemIndex: startItem,
      startPositionIndex: startPosition,
      endBlockIndex: endBlock,
      endItemIndex: endItem,
      endPositionIndex: endPosition
    });
    newSelection.assertValidRange(blockList);
    selection.setSelection(newSelection);
  },
  createEntity(type, options) {
    options.action = type;
    return this._runCommand(CreateEntityCommand, options);
  },
  getBlockById(id) {
    return this.get('blockList').findBy('id', id);
  },
  getEntityById(id) {
    // TODO - Move to blocklist
    let blockList, i;
    let entity = this.get('selection.firstSelectedBlock').findEntityById(id);
    if (entity) {
      return entity;
    }
    if (!entity) {
      blockList = this.get('blockList');
      for (i = 0; i < blockList.get('length'); i++) {
        entity = blockList.objectAt(i).findEntityById(id);
        if (entity) {
          return entity;
        }
      }
    }
  },
  setSelectionByEntityId(id) {
    return this._runCommand(SelectEntityCommand, {
      id
    });
  },
  updateEntity(id, options) {
    //TODO: GJ: rename to updateEntityById
    let entity = this.get('selection.firstSelectedBlock').findEntityById(id);
    if (entity) {
      entity.setProperties(options);
    }
  },
  deleteEntityById(id) {
    let selection = this.get('selection');
    this.get('blockList').deleteEntityById(id);
    selection.recalculateCurrentEditableEntity();
  },
  deleteAttachmentById(id) {
    this.get('attachmentList').deleteById(id);
    this.didChange();
  },
  retryAttachmentById(id) {
    this.get('attachmentList').retryById(id);
  },
  insertCharacter(character) {
    let result;
    Em.instrument('Composer.composerState.insertCharacter', null, function () {
      this.throttledCheckpoint();
      let options = this.get('_skips');
      options.character = character;
      this._resetSkips();
      return this._runCommand(InsertCharacterCommand, options);
    }, this);
    return result;
  },
  insertCharacters(text) {
    let result;
    for (let i = 0; i < text.length; i++) {
      result = this.insertCharacter(text[i]);
    }
    return result;
  },
  paste(string, options) {
    options = options || {};
    this.checkpoint();
    options.string = string;
    options.uploadManager = this.get('uploadManager');
    return this._runCommand(PasteCommand, options);
  },
  cut() {
    if (this.get('selection.hasWidth') || this.get('selection.isSingleUneditableBlock')) {
      this.checkpoint();
      this.backspace();
      if (this.get('selection.firstSelectedBlock.isEmpty')) {
        this.backspace();
      }
    }
  },
  convertSelectionToBlock(type) {
    let options = {};
    this.checkpoint();
    this._resetSkips();
    options.blockType = type;
    return this._runCommand(ConvertSelectionCommand, options);
  },
  createBlock(type, options) {
    options = options || {};
    this.checkpoint();
    this._resetSkips();
    options.blockType = type;
    options.uploadManager = this.get('uploadManager');
    return this._runCommand(CreateBlockCommand, options);
  },
  insertBlock(block, options) {
    options = options || {};
    if (!options.index) {
      options.index = 0;
    }
    if (!block) {
      throw new Error('You must specify a block');
    }
    options.block = block;
    this.checkpoint();
    this._resetSkips();
    if (block.get('isAttachment')) {
      this.get('attachmentList').pushObject(block);
      this.didChange();
    } else {
      return this._runCommand(InsertBlockCommand, options);
    }
  },
  insertBlocks(blocks) {
    let options = {};
    this.checkpoint();
    options.blocks = BlockList.deserialize(blocks, this.get('config'), this.get('uploadManager')).content;
    let newAttachments = AttachmentList.deserialize(blocks, this.get('config'), this.get('uploadManager'));
    if (newAttachments.length > 0) {
      this.attachmentList.pushObjects(newAttachments.content);
    }
    return this._runCommand(InsertBlocksCommand, options);
  },
  insertBlocksAtMarker(options = {}) {
    this.checkpoint();
    let newAttachments = AttachmentList.deserialize(options.blocks, this.get('config'), this.get('uploadManager'));
    if (newAttachments.length > 0) {
      this.attachmentList.pushObjects(newAttachments.content);
    }
    options.blocks = BlockList.deserialize(options.blocks, this.get('config'), this.get('uploadManager')).content;
    return this._runCommand(InsertBlocksAtMarkerCommand, options);
  },
  insertParagraphAtIndex(index) {
    this.checkpoint();
    let paragraphBlock = this.get('blockList.defaultBlockType').create({
      config: this.get('config')
    });
    return this._runCommand(InsertBlockCommand, {
      index,
      selectNewBlock: true,
      block: paragraphBlock
    });
  },
  insertAttribute(attribute) {
    this.checkpoint();
    this._resetSkips();
    return this._runCommand(InsertAttributeCommand, {
      attribute
    });
  },
  anchor(href) {
    this.checkpoint();
    this._resetSkips();
    let options = {
      action: 'anchor',
      href
    };
    if (this.get('selection.hasWidth')) {
      return this._runCommand(CreateEntityCommand, options);
    }
  },
  bold(force) {
    force = force || false;
    this.checkpoint();
    let options = {
      forceEntity: this._shouldForceEntity('bold') || force,
      action: 'bold'
    };
    if (this.get('selection').caretIsAtEndOfEntityType('bold')) {
      this.set('skipBold', true);
    }
    return this._runCommand(CreateEntityCommand, options);
  },
  italic(force) {
    force = force || false;
    this.checkpoint();
    let options = {
      forceEntity: this._shouldForceEntity('italic') || force,
      action: 'italic'
    };
    if (this.get('selection').caretIsAtEndOfEntityType('italic')) {
      this.set('skipItalic', true);
    }
    return this._runCommand(CreateEntityCommand, options);
  },
  mention(options) {
    this._resetSkips();
    options.action = 'mention';
    return this._runCommand(CreateEntityCommand, options);
  },
  marker(options) {
    options = options || {};
    this._resetSkips();
    options.action = 'marker';
    return this._runCommand(CreateEntityCommand, options);
  },
  delete() {
    let result;
    this.throttledCheckpoint();
    this._resetSkips();
    result = this._runCommand(DeleteCommand);
    return result;
  },
  deleteWord() {
    let result;
    this.throttledCheckpoint();
    this._resetSkips();
    result = this._runCommand(DeleteWordCommand);
    return result;
  },
  backspace() {
    let result;
    Em.instrument('Composer.composerState.backspace', null, function () {
      this.throttledCheckpoint();
      this._resetSkips();
      result = this._runCommand(BackspaceCommand);
    }, this);
    return result;
  },
  backspaceWord() {
    this.throttledCheckpoint();
    this._resetSkips();
    return this._runCommand(BackspaceWordCommand);
  },
  return() {
    this.checkpoint();
    this._resetSkips();
    return this._runCommand(ReturnCommand);
  },
  softReturn() {
    this.checkpoint();
    this.insertCharacter(Constants.NEW_LINE);
  },
  align(direction) {
    this.checkpoint();
    this._runCommand(AlignCommand, {
      direction
    });
  },
  moveSelectionPastEntityOrBlock() {
    let currentEditableObject = this.get('currentEditableObject');
    if (currentEditableObject) {
      let selection = this.get('bomSelection');
      if (currentEditableObject.get('isBlock')) {
        let newBlockIndex = selection.endBlockIndex + 1;
        if (this.get('blockList.length') <= newBlockIndex) {
          this.get('blockList').insertDefaultBlockAt(newBlockIndex);
        }
        this.setSelection(newBlockIndex, 0, 0, newBlockIndex, 0, 0);
      } else if (currentEditableObject.get('isAnchor')) {
        this.setSelection(selection.endBlockIndex, selection.endItemIndex, currentEditableObject.get('endIndex'), selection.endBlockIndex, selection.endItemIndex, currentEditableObject.get('endIndex'));
      } else {
        this.setSelection(selection.endBlockIndex, selection.endItemIndex, selection.endPositionIndex, selection.endBlockIndex, selection.endItemIndex, selection.endPositionIndex);
      }
    }
  },
  formattingOptionStates: Em.computed('selectionInfo', 'html', 'selection', 'selection.isAnchorActive', 'selection.isBoldActive', 'selection.isItalicActive', 'selection.firstSelectedBlock.isCentered', function () {
    return {
      bold: {
        isActive: this.get('selection.isBoldActive'),
        isEnabled: this._isBoldEnabled()
      },
      italic: {
        isActive: this.get('selection.isItalicActive'),
        isEnabled: this._isItalicEnabled()
      },
      anchor: {
        isActive: this.get('selection.isAnchorActive'),
        isEnabled: this._isAnchorEnabled()
      },
      heading: {
        isActive: this._isHeadingActive(),
        isEnabled: this._isHeadingEnabled()
      },
      subheading: {
        isActive: this._isSubheadingActive(),
        isEnabled: this._isSubheadingEnabled()
      },
      center: {
        isActive: this._isCenterActive(),
        isEnabled: this._isCenterEnabled()
      }
    };
  }),
  _isBoldEnabled() {
    if (!this.get('config.allowBold')) {
      return false;
    }
    return !this.get('selection.containsHeading');
  },
  _isItalicEnabled() {
    if (!this.get('config.allowItalic')) {
      return false;
    }
    return !this.get('selection.containsHeading');
  },
  _isAnchorEnabled() {
    return this.get('config.allowAnchor') && this.get('selection.isSingleBlock') && !this.get('selection').containsEntity('code');
  },
  _isHeadingEnabled() {
    if (!this.get('config.allowHeading')) {
      return false;
    }
    return this.get('selection.isSingleBlock') && !this.get('selection.firstSelectedBlock.isListType');
  },
  _isHeadingActive() {
    return this.get('selection.isSingleBlock') && this.get('selection.firstSelectedBlock.type') === 'heading';
  },
  _isSubheadingActive() {
    return this.get('selection.isSingleBlock') && this.get('selection.firstSelectedBlock.type') === 'subheading';
  },
  _isSubheadingEnabled() {
    if (!this.get('config.allowSubheading')) {
      return false;
    }
    return this.get('selection.isSingleBlock') && !this.get('selection.firstSelectedBlock.isListType');
  },
  _isCenterActive() {
    if (!this.get('config.allowAlignment')) {
      return false;
    }
    return this.get('selection.firstSelectedBlock.isCentered');
  },
  _isCenterEnabled() {
    return this.get('config.allowAlignment') && this.get('selection.firstSelectedBlock.canAlign');
  },
  _runCommand(Command, options, selection) {
    selection = selection || this.get('selection');
    let result = Command.create({
      blockList: this.get('blockList'),
      selection,
      config: this.get('config'),
      options
    }).run();
    if ('function' === typeof this.didChange) {
      this.didChange();
    }
    return result;
  },
  setEntitySkip(type) {
    this.set(`skip${Em.String.capitalize(type)}`, true);
  },
  _skips: Em.computed('skipBold', 'skipItalic', function () {
    return {
      skipBold: this.get('skipBold'),
      skipItalic: this.get('skipItalic')
    };
  }),
  _resetSkips() {
    this.set('skipBold', false);
    this.set('skipItalic', false);
  },
  _shouldForceEntity(type) {
    let sel = this.get('selection');
    if (sel.get('isMultiBlock')) {
      return sel.containsEntity(type) && !sel.coveredByEntity(type);
    }
    return false;
  },
  checkpointData: Em.computed('jsonBlocksForUndo', 'selection.info', function () {
    let data = {
      jsonBlocks: this.get('jsonBlocksForUndo'),
      selection: this.get('selection').getSelectionHash()
    };
    return JSON.stringify(data);
  }),
  restoreCheckpoint(data) {
    data = JSON.parse(data);
    this.get('blockList').restoreCheckpoint(data.jsonBlocks, this.get('uploadManager'));
    this.get('selection').restoreCheckpoint(data.selection);
  }
});